import { Controller } from "@hotwired/stimulus"

// The QTAG shop's rolls stepper (TASK-195.2). Echoes the tag count, the
// reserved-reference preview and the order summary as the quantity changes.
// Display only — the server re-prices from Pricing and validates the rolls on
// submit, so nothing here is trusted.
export default class extends Controller {
  static targets = ["rolls", "tags", "range", "line", "subtotal", "vat", "total"]
  static values = {
    unitPence: Number,
    vatRate: Number,
    rollSize: Number,
    maxRolls: Number,
    startReference: String
  }

  connect() {
    this.money = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" })
    this.count = new Intl.NumberFormat("en-GB")
    this.update()
  }

  decrement() {
    this.step(-1)
  }

  increment() {
    this.step(1)
  }

  step(delta) {
    const current = this.parsedRolls() ?? 1
    const next = Math.min(this.maxRollsValue, Math.max(1, current + delta))
    this.rollsTarget.value = next
    this.update()
  }

  update() {
    const rolls = this.parsedRolls()
    if (rolls === null) {
      this.render(null)
      return
    }
    this.render(rolls)
  }

  // A whole number of rolls in range, or null — the same rule the server applies.
  parsedRolls() {
    const raw = this.rollsTarget.value.trim()
    if (!/^\d+$/.test(raw)) return null

    const rolls = parseInt(raw, 10)
    return rolls >= 1 && rolls <= this.maxRollsValue ? rolls : null
  }

  render(rolls) {
    const dash = "—"
    const tags = rolls === null ? null : rolls * this.rollSizeValue
    const subtotal = rolls === null ? null : rolls * this.unitPenceValue
    const vat = subtotal === null ? null : Math.round(subtotal * this.vatRateValue)

    if (this.hasTagsTarget) this.tagsTarget.textContent = tags === null ? dash : `${this.count.format(tags)} tags`
    if (this.hasRangeTarget) this.rangeTarget.textContent = tags === null ? dash : this.referenceRange(tags)
    if (this.hasLineTarget) {
      this.lineTarget.textContent = `Roll of ${this.count.format(this.rollSizeValue)} QTAG labels × ${rolls ?? dash}`
    }
    this.subtotalTargets.forEach((el) => { el.textContent = this.formatPence(subtotal) })
    if (this.hasVatTarget) this.vatTarget.textContent = this.formatPence(vat)
    if (this.hasTotalTarget) this.totalTarget.textContent = this.formatPence(subtotal === null ? null : subtotal + vat)
  }

  formatPence(pence) {
    return pence === null ? "—" : this.money.format(pence / 100)
  }

  // Mirrors QtagOrder.advance_reference: A999999 rolls over to B000001.
  referenceRange(quantity) {
    const start = this.startReferenceValue
    if (!/^[A-Z]\d{6}$/.test(start)) return "—"

    let letter = start.charCodeAt(0)
    let number = parseInt(start.slice(1), 10) + quantity - 1
    while (number > 999999) {
      number -= 999999
      letter += 1
    }
    const end = `${String.fromCharCode(letter)}${String(number).padStart(6, "0")}`
    return `${start} – ${end}`
  }
}
